import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { Estimation } from '../../models/estimacion.model';
import { User } from '../../models/user.model';
import { AuthService } from '../../auth/auth.service';
import { environment } from '../../../environments/environment';

import {
  Exporter,
  ExporterDescriptor,
  ExportArtifact,
  SyncExporter
} from '../models/exporter';
import { ExportPayload, ExportTaskRecord } from '../models/export-payload';
import { EXPORTERS } from '../tokens/exporter.tokens';
import { ExportPayloadBuilder } from './export-payload.builder';

/** Descriptor extendido con el flag de autorización, para el panel. */
export type ExporterDescriptorAuth = ExporterDescriptor & { authorized: boolean };

/** Respuesta del backend al encolar un job async. */
export interface AsyncJobEnqueuedResponse {
  jobId: string;
  status: string;
  createdAt: string;
}

/** Args para startAsyncJob — todo lo necesario para que el backend procese. */
export interface StartAsyncJobArgs {
  exporterId: string;
  estimationId: string;
  options: unknown;
  payload: ExportPayload;
}

/** Respuesta del endpoint /export-extras del backend. */
interface ExportExtrasResponse {
  estimationId: string;
  tasks: Array<{ id: string; [k: string]: unknown }>;
}

/**
 * Orquestador único del módulo de exportación.
 *
 * Responsabilidades:
 *   - Registro de exporters (DI multi-provider).
 *   - RBAC consultando AuthService.
 *   - Construcción del ExportPayload canónico.
 *   - Ejecución de exporters síncronos (CSV/XLSX/JSON, en cliente).
 *   - Encolado de jobs asíncronos contra el backend (Jira/GitLab, vía POST).
 *   - Enriquecimiento opcional del payload con extras del servidor (toggle
 *     "Incluir campos completos").
 *   - Descarga de archivos (File System Access API + fallback anchor+click).
 *
 * Refactor Punto 6.B.2: `exportAsync()` (que llamaba a AsyncExporter.submit)
 * desaparece — la lógica async vive 100% en el backend ahora. En su lugar,
 * `startAsyncJob()` hace POST a /api/exports/jobs y el ExportJobTracker se
 * encarga del SSE.
 */
@Injectable({ providedIn: 'root' })
export class ExportService {

  private http = inject(HttpClient);

  constructor(
    @Optional() @Inject(EXPORTERS) private exporters: Exporter[] | null,
    private auth: AuthService,
    private payloadBuilder: ExportPayloadBuilder
  ) {}

  // ==========================================================================
  // REGISTRO DE EXPORTERS
  // ==========================================================================

  /** Descriptores autorizados, ordenados. */
  getAvailableDescriptors(): ExporterDescriptor[] {
    return (this.exporters ?? [])
      .map(e => e.descriptor)
      .filter(d => this.auth.hasPermission(d.requiredPermission))
      .sort((a, b) => a.order - b.order);
  }

  /** Todos los descriptores, con flag `authorized`. */
  getAllDescriptors(): ExporterDescriptorAuth[] {
    return (this.exporters ?? [])
      .map(e => ({
        ...e.descriptor,
        authorized: this.auth.hasPermission(e.descriptor.requiredPermission)
      }))
      .sort((a, b) => a.order - b.order);
  }

  // ==========================================================================
  // PAYLOAD
  // ==========================================================================

  /** Construye el payload canónico a partir del Borrador actual. */
  buildPayload(estimation: Estimation, user: User | null | undefined): ExportPayload {
    return this.payloadBuilder.build(estimation, user);
  }

  /**
   * Enriquece un payload con campos extras del servidor (toggle "completo").
   * Hace GET a /api/estimations/:id/export-extras y mergea por task.id.
   * Cliente gana sobre servidor en campos coincidentes — preservación del Borrador.
   *
   * Marca el payload con `extrasIncluded: true` para que cada exporter
   * sepa que debe renderizar las columnas/campos extras. Decisión β del
   * Punto 8 — marcador explícito > inferir por presencia de claves.
   *
   * Si el endpoint falla o devuelve lista vacía, devuelve el payload tal
   * cual (extrasIncluded queda como estaba, típicamente false).
   */
  async enrichPayloadWithExtras(payload: ExportPayload): Promise<ExportPayload> {
    try {
      const url = `${environment.api_service_estimations}/${payload.estimation.id}/export-extras`;
      const extras = await firstValueFrom(this.http.get<ExportExtrasResponse>(url));

      if (!extras || !Array.isArray(extras.tasks) || extras.tasks.length === 0) {
        return payload;
      }

      const extrasById = new Map<string, Record<string, unknown>>();
      for (const t of extras.tasks) {
        if (t && t.id) extrasById.set(String(t.id), t);
      }

      const mergedTasks: ExportTaskRecord[] = payload.tasks.map(task => {
        const extra = extrasById.get(String(task.id));
        if (!extra) return task;
        // Cliente gana: extras solo aporta campos AUSENTES en la task del cliente.
        const merged: Record<string, unknown> = { ...extra, ...task };
        // Cast vía unknown: TS estricto no acepta el casting directo entre
        // Record<string, unknown> y la interfaz tipada — es seguro porque
        // `task` (ya tipada como ExportTaskRecord) sobreescribe todos los
        // campos requeridos del extra.
        return merged as unknown as ExportTaskRecord;
      });

      // Marcador explícito: los exporters consultan `payload.extrasIncluded`
      // para decidir si añaden columnas/campos extras.
      return { ...payload, tasks: mergedTasks, extrasIncluded: true };
    } catch (err) {
      console.warn('[ExportService] enrichPayloadWithExtras falló:', err);
      return payload;
    }
  }

  // ==========================================================================
  // EJECUCIÓN
  // ==========================================================================

  /** Ejecuta un exporter síncrono y devuelve el ExportArtifact resultante. */
  async exportSync(id: string, payload: ExportPayload, options: unknown): Promise<ExportArtifact> {
    const exp = this.getAuthorizedExporter(id);
    if (!exp) throw new Error(`Exporter "${id}" no disponible o sin permisos.`);
    if (exp.descriptor.mode !== 'sync') {
      throw new Error(`Exporter "${id}" no es síncrono.`);
    }
    return (exp as SyncExporter).generate(payload, options);
  }

  /**
   * Encola un job asíncrono en el backend. Devuelve { jobId, status, createdAt }.
   *
   * El caller (ExportPanel) emite `(enqueued)` con el jobId y cierra el panel.
   * El host (EstimacionComponent) recibe el evento y arranca el ExportJobTracker
   * con `track(jobId, ...)` para suscribirse al SSE.
   */
  async startAsyncJob(args: StartAsyncJobArgs): Promise<AsyncJobEnqueuedResponse> {
    const exp = this.getAuthorizedExporter(args.exporterId);
    if (!exp) {
      throw new Error(`Exporter "${args.exporterId}" no disponible o sin permisos.`);
    }
    if (exp.descriptor.mode !== 'async') {
      throw new Error(`Exporter "${args.exporterId}" no es asíncrono.`);
    }

    return firstValueFrom(
      this.http.post<AsyncJobEnqueuedResponse>(`${environment.api_service_exports}/jobs`, {
        estimationId: args.estimationId,
        exporterId: args.exporterId,
        options: args.options ?? {},
        payload: args.payload
      })
    );
  }

  // ==========================================================================
  // DESCARGA (síncronos)
  // ==========================================================================

  /**
   * Dispara la descarga. File System Access API si está disponible (Chrome,
   * Edge, Opera), fallback anchor+click en el resto.
   */
  async triggerDownload(artifact: ExportArtifact): Promise<void> {
    const w = window as any;

    if (typeof w.showSaveFilePicker === 'function') {
      try {
        const ext = this.getExtension(artifact.filename);
        const handle = await w.showSaveFilePicker({
          suggestedName: artifact.filename,
          types: [{
            description: ext ? ext.toUpperCase() : 'Archivo',
            accept: { [artifact.mimeType]: ext ? [`.${ext}`] : [] }
          }]
        });
        const writable = await handle.createWritable();
        await writable.write(artifact.blob);
        await writable.close();
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') return;     // user canceled picker
        // cualquier otro fallo: caer a anchor click.
      }
    }
    this.fallbackAnchorDownload(artifact);
  }

  private fallbackAnchorDownload(artifact: ExportArtifact): void {
    const url = URL.createObjectURL(artifact.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }

  private getExtension(filename: string): string | null {
    const idx = filename.lastIndexOf('.');
    if (idx < 0 || idx === filename.length - 1) return null;
    return filename.slice(idx + 1).toLowerCase();
  }

  private getAuthorizedExporter(id: string): Exporter | null {
    const e = (this.exporters ?? []).find(x => x.descriptor.id === id);
    if (!e) return null;
    if (!this.auth.hasPermission(e.descriptor.requiredPermission)) return null;
    return e;
  }
}